import { formatDate, parseUtcDate, resolveOtherInfoObj } from '@moko/utils';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  IAsset,
  IDateTimeRange,
  ITimeslot,
} from '../../services/assetServices';
import { ITrackingDevice } from '../../services/trackingService';
import { IMetadata, getMetadata } from '../../services/metadataService';
import { getRenter } from '../../services/renterService';
import {
  ICreateRentRequest,
  IUpdateRentRequest,
  createRent,
  editRent,
  getRent,
  IRent,
} from '../../services/rentService';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  DatePicker,
  IconButton,
  Label,
  Pivot,
  PivotItem,
  Stack,
  Text,
  TimePicker,
} from '@fluentui/react';
import { useLocation } from 'react-router-dom';
import {
  labelColumnStyle,
  valueColumnStyle,
} from '../../common/styles/FormsStyles';
import AssetSelector from '../works/AssetSelector';
import RentMap from './RentMap';
import TrackingDeviceSelector from '../../common/selectors/TrackingDeviceSelector';
import TimeslotSelector from './TimeslotSelector';
import { useBoolean } from '@fluentui/react-hooks';
import AppContext from '../AppContext';
import { EditItem } from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';
import { Field, Input } from '@fluentui/react-components';

const Rent = () => {
  const query = new URLSearchParams(useLocation().search);
  const itemId = Number.parseInt(query.get('id') ?? '');
  const renterId = Number.parseInt(query.get('renterId') ?? '');

  const [metadata, setMetadata] = useState<IMetadata>();
  const [otherInfo, setOtherInfo] = useState({});

  const [rentId, setRentId] = useState<number>(itemId);
  const [renterName, setRenterName] = useState<string>();

  const [isRentLoaded, setIsRentLoaded] = useState<boolean>(false);

  const setSuccessMessage = useSetAtom(successMessageAtom);

  const [assetId, setAssetId] = useState<number>();
  const [assetName, setAssetName] = useState<string>();

  const [startTime, setStartTime] = useState<Date>();
  const [endTime, setEndTime] = useState<Date>();
  const [isStarted, setIsStarted] = useState<boolean>(false);

  const [trackingDeviceId, setTrackingDeviceId] = useState<number>();
  const [trackingDeviceName, setTrackingDeviceName] = useState<string>();

  const [
    isTimeslotSelectorOpen,
    { setTrue: showTimeslotSelector, setFalse: hideTimeslotSelector },
  ] = useBoolean(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState<IDateTimeRange>({
    startTime: new Date(),
    endTime: new Date(),
  });

  const isSaved = useRef(false);

  const handleCancelAsset = () => {
    setAssetId(undefined);
    setAssetName(undefined);
  };

  const handleCancelTracking = () => {
    setTrackingDeviceId(-1);
    setTrackingDeviceName(undefined);
  };

  const context = useContext(AppContext);

  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);

    try {
      const abortController = new AbortController();

      const metadata = await getMetadata(abortController, 'Rent');
      setMetadata(metadata);

      const renter = await getRenter(abortController, renterId);
      setRenterName(renter.name);

      if (rentId > 0) {
        const rent: IRent = await getRent(abortController, rentId);
        setAssetId(rent.assetId);
        setAssetName(rent.assetName);
        setTrackingDeviceId(rent.trackingDeviceId);
        setTrackingDeviceName(rent.trackingDeviceName);
        const from = parseUtcDate(String(rent.fromDate));
        const to = parseUtcDate(String(rent.toDate));
        setSelectedTimeslot({
          startTime: from,
          endTime: to,
        });

        setStartTime(from);
        setEndTime(to);
        setIsStarted(from <= new Date());

        setOtherInfo(resolveOtherInfoObj(rent.otherInfo) ?? {});
        setIsRentLoaded(true);
        context.setSelectedTab(`Renting '${rent.assetName}' by ${renter.name}`);
      } else {
        setIsRentLoaded(true);
        context.setSelectedTab(`New rent by ${renter.name}`);
      }
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <Pivot>
        <PivotItem headerText="Details">
          {isRentLoaded ? (
            <div>
              <EditItem
                preButtons={[
                  {
                    text: `Time ${
                      assetId === undefined || assetId === 0 ? ' *' : ''
                    }`,
                    onClick: function (): void {
                      showTimeslotSelector();
                    },
                    disabled: assetId === undefined || assetId === 0,
                    iconProps: { iconName: 'DateTime' },
                  } as any,
                ]}
                getAction={async (abortController, id) => {
                  const rent: IRent = await getRent(abortController, id);
                  setRentId(id);
                  setAssetName(rent.assetName);
                  setRenterName(rent.renterName);
                  setTrackingDeviceName(rent.trackingDeviceName);
                  setAssetId(rent.assetId);
                  setOtherInfo(resolveOtherInfoObj(rent.otherInfo) ?? {});
                  rent.statusId = rent.statusId;
                  return rent;
                }}
                newAction={async (abortController, newRent) => {
                  const data: ICreateRentRequest = {
                    assetId: assetId ?? 0,
                    renterId: renterId,
                    trackingDeviceId: trackingDeviceId,
                    code: newRent.code as string,
                    description: newRent.description as string,
                    otherInfo: JSON.stringify(otherInfo),
                    agreedPrice: Number.isNaN(
                      Number.parseFloat(newRent.agreedPrice as string)
                    )
                      ? undefined
                      : Number.parseFloat(newRent.agreedPrice as string),
                    agreedPriceUnit: newRent.agreedPriceUnit as string,
                    fromDate:
                      selectedTimeslot?.startTime ??
                      (newRent.fromDate as string),
                    toDate:
                      selectedTimeslot?.endTime ?? (newRent.toDate as string),
                    statusId: Number.parseInt(newRent.statusId as string),
                    status: newRent.statusId as string,
                  };
                  const newRentResponse = await createRent(
                    abortController,
                    data
                  );
                  setSuccessMessage(
                    `Rent with id: ${newRentResponse.rentId} created.`
                  );
                }}
                editAction={async (abortController, id, editedRent) => {
                  const data: IUpdateRentRequest = {
                    assetId: assetId ?? 0,
                    renterId: renterId,
                    description: editedRent.description as string,
                    trackingDeviceId: trackingDeviceId,
                    code: editedRent.code as string,
                    otherInfo: JSON.stringify(otherInfo),
                    agreedPrice: Number.isNaN(
                      Number.parseFloat(editedRent.agreedPrice as string)
                    )
                      ? undefined
                      : Number.parseFloat(editedRent.agreedPrice as string),
                    agreedPriceUnit: editedRent.agreedPriceUnit as string,
                    fromDate:
                      selectedTimeslot?.startTime ??
                      (editedRent.fromDate as string),
                    toDate:
                      selectedTimeslot?.endTime ??
                      (editedRent.toDate as string),
                    statusId: Number.parseInt(editedRent.statusId as string),
                    status: editedRent.status as string,
                  };
                  await editRent(abortController, id, data);
                  setSuccessMessage(
                    `Rent with id: ${id} successfully updated.`
                  );
                }}
                isSaved={isSaved}
                back={`/rents?id=${renterId}`}
                metadata={{
                  fields: [
                    {
                      name: 'description',
                      fieldType: 'String',
                      label: 'Description',
                    },
                    {
                      name: 'code',
                      fieldType: 'String',
                      label: 'Code',
                    },
                    {
                      name: 'agreedPrice',
                      fieldType: 'Number',
                      label: 'Agreed Price',
                    },
                    {
                      name: 'agreedPriceUnit',
                      fieldType: 'String',
                      label: 'Price Unit',
                    },
                    {
                      name: 'statusId',
                      fieldType: 'Lookup',
                      label: 'Status Id',
                      lookupList: 'RentStatuses',
                    },
                    {
                      name: 'status',
                      fieldType: 'String',
                      label: 'Status',
                    },
                  ],
                  lookups: [
                    {
                      name: 'RentStatuses',
                      values: [
                        {
                          key: '1',
                          value: 'Pending',
                        },
                        {
                          key: '2',
                          value: 'Rented',
                        },
                        {
                          key: '3',
                          value: 'Returned',
                        },
                        {
                          key: '4',
                          value: 'Canceled',
                        },
                      ],
                    },
                  ],
                  validations: [],
                  interactions: [],
                }}
              >
                <>
                  <Stack
                    tokens={{ childrenGap: 5 }}
                    styles={{ root: { width: 350 } }}
                  >
                    <Field label={'Renter'} orientation="horizontal">
                      <Input value={renterName} readOnly disabled />
                    </Field>
                    {/* Asset Selector */}
                    <Stack horizontal styles={{ root: { width: 350 } }}>
                      <AssetSelector
                        disabled={isSaved.current || isStarted}
                        onSelected={(asset: IAsset) => {
                          setAssetId(asset.id);
                          setAssetName(asset.name);
                        }}
                        assetName={assetName}
                      />
                      <IconButton
                        disabled={isSaved.current || isStarted}
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={handleCancelAsset}
                      />
                    </Stack>
                    {/* Tracking device selector */}
                    <Stack horizontal>
                      <TrackingDeviceSelector
                        disabled={isSaved.current || isStarted}
                        onSelected={(trackingDevice: ITrackingDevice) => {
                          setTrackingDeviceId(trackingDevice.id);
                          setTrackingDeviceName(
                            trackingDevice.code +
                              (!trackingDevice.name
                                ? ''
                                : '|' + trackingDevice.name)
                          );
                        }}
                        trackingDeviceName={trackingDeviceName}
                      />
                      <IconButton
                        disabled={isSaved.current || isStarted}
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={handleCancelTracking}
                      />
                    </Stack>
                    {/* From date selector */}
                    <Stack horizontal>
                      <Label style={labelColumnStyle}>From date</Label>
                      <Stack
                        horizontal
                        style={valueColumnStyle}
                        tokens={{ childrenGap: 5 }}
                      >
                        <DatePicker
                          disabled={isSaved.current || isStarted}
                          styles={{
                            root: { height: 32, width: 150 },
                            textField: { width: 150 },
                          }}
                          placeholder={
                            rentId && selectedTimeslot?.startTime
                              ? formatDate(selectedTimeslot.startTime)
                              : undefined
                          }
                          ariaLabel="Select a date"
                          isMonthPickerVisible={false}
                          onSelectDate={(
                            selectedDate: Date | null | undefined
                          ) => {
                            if (selectedDate) {
                              setSelectedTimeslot({
                                startTime: selectedDate,
                                endTime: selectedTimeslot?.endTime,
                              });
                            }
                          }}
                        />
                        <TimePicker
                          disabled={isSaved.current || isStarted}
                          defaultValue={rentId ? startTime : undefined}
                          onChange={(_, time) => {
                            const newStartTime = new Date(
                              selectedTimeslot.startTime
                            );
                            newStartTime.setHours(time.getHours());
                            newStartTime.setMinutes(time.getMinutes());
                            newStartTime.setSeconds(0);
                            setSelectedTimeslot({
                              startTime: newStartTime,
                              endTime: selectedTimeslot?.endTime,
                            });
                          }}
                        />
                      </Stack>
                    </Stack>
                    {/* To date selector */}
                    <Stack horizontal>
                      <Label style={labelColumnStyle}>To date</Label>
                      <Stack
                        horizontal
                        style={valueColumnStyle}
                        tokens={{ childrenGap: 5 }}
                      >
                        <DatePicker
                          disabled={isSaved.current || isStarted}
                          styles={{
                            root: { height: 32, width: 150 },
                            textField: { width: 150 },
                          }}
                          placeholder={
                            rentId && selectedTimeslot?.endTime
                              ? formatDate(selectedTimeslot.endTime)
                              : undefined
                          }
                          ariaLabel="Select a date"
                          isMonthPickerVisible={false}
                          onSelectDate={(
                            selectedDate: Date | null | undefined
                          ) => {
                            if (selectedDate) {
                              setSelectedTimeslot({
                                startTime: selectedTimeslot?.startTime,
                                endTime: selectedDate,
                              });
                            }
                          }}
                        />
                        <TimePicker
                          disabled={isSaved.current || isStarted}
                          defaultValue={rentId ? endTime : undefined}
                          onChange={(_, time) => {
                            const newEndTime = new Date(
                              selectedTimeslot.endTime
                            );
                            newEndTime.setHours(time.getHours());
                            newEndTime.setMinutes(time.getMinutes());
                            newEndTime.setSeconds(0);
                            setSelectedTimeslot({
                              startTime: selectedTimeslot?.startTime,
                              endTime: newEndTime,
                            });
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                  {metadata ? (
                    <OtherInfo
                      metadata={metadata}
                      otherInfo={JSON.stringify(otherInfo)}
                      isSaved={isSaved}
                      onOtherInfoChanged={(newValue: string) =>
                        setOtherInfo(newValue)
                      }
                    />
                  ) : (
                    <></>
                  )}
                </>
              </EditItem>
              {assetId !== undefined && assetId !== 0 ? null : (
                <Text variant="small">
                  * Asset should be selected to be able to use timeselector
                </Text>
              )}
            </div>
          ) : null}
        </PivotItem>
        <PivotItem headerText="Location">
          <RentMap rentId={rentId} />
        </PivotItem>
      </Pivot>
      {assetId !== undefined && assetId !== 0 && (
        <TimeslotSelector
          assetId={assetId ?? 0}
          isOpen={isTimeslotSelectorOpen}
          hideTimeslotSelector={hideTimeslotSelector}
          onTimeslotSelected={(selectedTimeslot: ITimeslot) => {
            if (selectedTimeslot.rentId !== null) {
              return;
            }
            setSelectedTimeslot(selectedTimeslot);
            hideTimeslotSelector();
          }}
        />
      )}
    </>
  );
};

export default Rent;
